import React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useUploads } from '../../helpers/UploadManager';
import './UploadWidget.css';

const IconArrowUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
  </svg>
);

const IconVerticalPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
    <rect x="7" y="2" width="10" height="20" rx="2.5" ry="2.5" />
    <polygon points="11 9.5 14.5 12 11 14.5 11 9.5" fill="currentColor" stroke="none" />
  </svg>
);

function SinglePill({ items, isVertical }) {
  const location = useLocation();
  const history = useHistory();

  if (!items || items.length === 0) return null;
  if (!isVertical && location.pathname === '/desktop-upload') return null;

  const active = items.filter(u => ['queued', 'uploading', 'paused', 'processing'].includes(u.status));
  const failed = items.filter(u => u.status === 'failed');
  const done   = items.filter(u => u.status === 'clean');

  const totalBatchBytes = items.reduce((s, u) => s + (u.fileSize || 0), 0);
  const uploadedBatchBytes = items.reduce((s, u) => {
    if (u.status === 'clean') return s + (u.fileSize || 0);
    return s + (u.uploadedBytes || 0);
  }, 0);
  const pct = totalBatchBytes > 0 ? Math.round((uploadedBatchBytes / totalBatchBytes) * 100) : 0;

  let label, subLabel;
  let pillClass = `desktop-upload-pill ${isVertical ? 'desktop-upload-pill--vertical' : ''}`;

  const prefix = isVertical ? 'Vertical: ' : '';

  if (active.length > 0) {
    label = `${prefix}${active.length} upload${active.length !== 1 ? 's' : ''} in progress`;
    subLabel = `${pct}%`;
  } else if (failed.length > 0) {
    label = `${prefix}${failed.length} upload${failed.length !== 1 ? 's' : ''} failed`;
    subLabel = 'Click to retry';
    pillClass += ' desktop-upload-pill--error';
  } else {
    label = `${prefix}${done.length} upload${done.length !== 1 ? 's' : ''} complete`;
    subLabel = 'Click to view';
    pillClass += ' desktop-upload-pill--done';
  }

  const handleClick = (e) => {
    if (isVertical) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('open-wasabi-vertical-modal'));
      if (location.pathname !== '/vertical-shows/videos/list') {
        history.push('/vertical-shows/videos/list', { openWasabiModal: true });
      }
    }
  };

  const targetLink = isVertical ? '/vertical-shows/videos/list' : '/desktop-upload';
  const titleText = isVertical ? 'Go to Vertical Show Episodes Upload' : 'Go to Upload to Wasabi';

  return (
    <Link to={targetLink} onClick={handleClick} className={pillClass} title={titleText}>
      <svg className="desktop-upload-pill__ring-svg" viewBox="0 0 36 36" style={{ width: 32, height: 32, flexShrink: 0 }}>
        <circle cx="18" cy="18" r="15" fill="none" className="desktop-upload-pill__ring-track" strokeWidth="3" />
        {active.length > 0 && (
          <circle
            cx="18" cy="18" r="15"
            fill="none"
            className="desktop-upload-pill__ring-progress"
            strokeWidth="3"
            strokeDasharray={`${Math.round(pct * 0.942)} 100`}
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
          />
        )}
        <foreignObject x="8" y="8" width="20" height="20">
          <div className="desktop-upload-pill__icon-container">
            {isVertical ? <IconVerticalPhone /> : <IconArrowUp />}
          </div>
        </foreignObject>
      </svg>

      <div className="desktop-upload-pill__body">
        <span className="desktop-upload-pill__label">{label}</span>
        <span className="desktop-upload-pill__sub">{subLabel}</span>
      </div>
    </Link>
  );
}

export default function UploadWidget() {
  const { uploads } = useUploads();
  const all = Object.values(uploads);

  if (all.length === 0) return null;

  const normalItems = all.filter(u => !u.isVertical);
  const verticalItems = all.filter(u => u.isVertical);

  return (
    <div className="desktop-upload-widget-stack">
      <SinglePill items={verticalItems} isVertical={true} />
      <SinglePill items={normalItems} isVertical={false} />
    </div>
  );
}
